import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import {
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Users,
  MapPin,
  Loader2,
  Video,
  Building,
  QrCode
} from 'lucide-react';

const AdminEventos = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'presencial',
    date: '',
    time: '',
    location: '',
    price: 0,
    total_spots: 100
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/admin/events?upcoming_only=false');
      setEvents(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los eventos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        date: formData.date ? new Date(formData.date).toISOString() : null,
        price: parseFloat(formData.price) || 0,
        total_spots: parseInt(formData.total_spots) || null
      };

      if (editingEvent) {
        await api.put(`/admin/events/${editingEvent.id}`, payload);
        toast({ title: "Evento actualizado" });
      } else {
        await api.post('/admin/events', payload);
        toast({ title: "Evento creado" });
      }
      
      fetchEvents();
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error al guardar",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este evento?')) return;
    
    try {
      await api.delete(`/admin/events/${id}`);
      toast({ title: "Evento eliminado" });
      fetchEvents();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || '',
      description: event.description || '',
      event_type: event.event_type || 'presencial',
      date: event.date ? event.date.split('T')[0] : '',
      time: event.time || '',
      location: event.location || '',
      price: event.price || 0,
      total_spots: event.total_spots || 100
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      event_type: 'presencial',
      date: '',
      time: '',
      location: '',
      price: 0,
      total_spots: 100
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Eventos</h1>
            <p className="text-gray-600">Gestiona eventos y capacitaciones</p>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-gray-900">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Evento
          </Button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <Card>
            <CardHeader>
              <CardTitle>{editingEvent ? 'Editar Evento' : 'Nuevo Evento'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Título</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Tipo</label>
                    <select
                      className="w-full rounded-md border border-gray-300 p-2"
                      value={formData.event_type}
                      onChange={(e) => setFormData({...formData, event_type: e.target.value})}
                    >
                      <option value="presencial">Presencial</option>
                      <option value="online">Online</option>
                      <option value="hybrid">Híbrido</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Descripción</label>
                  <textarea
                    className="w-full rounded-md border border-gray-300 p-2"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </div>

                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Fecha</label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Hora</label>
                    <Input
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      placeholder="10:00"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Precio (MXN)</label>
                    <Input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Lugares</label>
                    <Input
                      type="number"
                      value={formData.total_spots}
                      onChange={(e) => setFormData({...formData, total_spots: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Ubicación</label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Centro de Convenciones CDMX"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-gray-900">
                    {editingEvent ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Events List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : events.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No hay eventos creados</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {events.map(event => (
              <Card key={event.id} className="overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Badge className={event.event_type === 'online' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                          {event.event_type === 'online' ? (
                            <><Video className="h-3 w-3 mr-1" /> Online</>
                          ) : (
                            <><Building className="h-3 w-3 mr-1" /> Presencial</>
                          )}
                        </Badge>
                        {event.price === 0 && (
                          <Badge variant="outline">Gratis</Badge>
                        )}
                        {!event.is_active && (
                          <Badge className="bg-red-100 text-red-700">Inactivo</Badge>
                        )}
                      </div>
                      
                      <h3 className="text-lg font-bold text-gray-900">{event.title}</h3>
                      <p className="text-gray-600 text-sm mt-1">{event.description}</p>
                      
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {event.date ? new Date(event.date).toLocaleDateString('es-MX') : 'Sin fecha'}
                          {event.time && ` - ${event.time}`}
                        </div>
                        {event.location && (
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1" />
                            {event.location}
                          </div>
                        )}
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-1" />
                          {event.available_spots}/{event.total_spots} lugares
                        </div>
                        {event.price > 0 && (
                          <div className="font-medium text-gray-900">
                            ${event.price.toLocaleString()} MXN
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex space-x-2 ml-4">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(event)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-600"
                        onClick={() => handleDelete(event.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEventos;
